import {
  ANALYSIS_LIMITS,
  type AnalysisResponse,
  type AnalyzeBacktestRequest,
  type TrustedArtifactLocation,
  type TrustedArtifactSource,
} from './analysis-contract'

export interface AnalysisWorkerLike {
  onmessage: ((event: MessageEvent) => void) | null
  onerror: ((event: ErrorEvent) => void) | null
  postMessage(message: unknown, transfer?: Transferable[]): void
  terminate(): void
}

export interface RunAnalysisOptions {
  signal?: AbortSignal
  timeoutMs?: number
  acquisitionTimeoutMs?: number
  fetcher?: typeof fetch
  workerFactory?: () => AnalysisWorkerLike
}

export class AnalysisCancelledError extends Error {
  constructor() {
    super('Analysis was cancelled.')
    this.name = 'AnalysisCancelledError'
  }
}

class AnalysisAcquisitionTimeout extends Error {}

function cancelled(request: AnalyzeBacktestRequest): AnalysisResponse {
  return {
    ...request,
    status: 'error',
    ok: false,
    code: 'cancelled',
    message: 'Analysis was cancelled.',
    counts: { scanned: 0, matched: 0, returned: 0 },
    completeness: { complete: false, truncated: false },
  }
}

function limit(
  request: AnalyzeBacktestRequest,
  message: string,
  suggestion: string,
): AnalysisResponse {
  return {
    ...request,
    status: 'limit',
    ok: false,
    limit: 'maxInputBytes',
    message,
    suggestion,
    counts: { scanned: 0, matched: 0, returned: 0 },
    completeness: { complete: false, truncated: true, reason: 'input_byte_limit' },
  }
}

function bytesFromSource(source: TrustedArtifactLocation): ArrayBuffer | undefined {
  if (!source.bytes) return undefined
  if (source.bytes instanceof ArrayBuffer) return source.bytes
  return source.bytes.buffer.slice(
    source.bytes.byteOffset,
    source.bytes.byteOffset + source.bytes.byteLength,
  )
}

async function readResponseBytes(
  response: Response,
  request: AnalyzeBacktestRequest,
  byteBudget = ANALYSIS_LIMITS.maxInputBytes,
  overLimitMessage = `Artifact exceeds ${ANALYSIS_LIMITS.maxInputBytes} bytes.`,
): Promise<ArrayBuffer | AnalysisResponse> {
  if (!response.ok) {
    if (response.status === 403 || response.status === 404) {
      return {
        ...request,
        status: 'unavailable',
        ok: false,
        reason: 'expired_location',
        message: `The owned ${request.artifact} artifact is unavailable (${response.status}).`,
        counts: { scanned: 0, matched: 0, returned: 0 },
        completeness: { complete: false, truncated: false },
      }
    }
    throw new Error(`Artifact fetch failed (${response.status}).`)
  }
  const declaredLengthHeader = response.headers.get('content-length')
  const declaredLength = declaredLengthHeader === null ? undefined : Number(declaredLengthHeader)
  if (
    declaredLength !== undefined &&
    Number.isFinite(declaredLength) &&
    declaredLength > byteBudget
  ) {
    return limit(
      request,
      overLimitMessage,
      'Use a smaller owned artifact or a narrower server-side artifact.',
    )
  }
  if (!response.body) {
    if (!Number.isFinite(declaredLength)) {
      return limit(
        request,
        'Artifact size could not be verified before reading it.',
        'Use an owned artifact response with a bounded content length.',
      )
    }
    const bytes = await response.arrayBuffer()
    if (bytes.byteLength > byteBudget) {
      return limit(request, overLimitMessage, 'Use a smaller owned artifact.')
    }
    return bytes
  }
  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let length = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (!value) continue
      length += value.byteLength
      if (length > byteBudget) {
        await reader.cancel()
        return limit(request, overLimitMessage, 'Use a smaller owned artifact.')
      }
      chunks.push(value)
    }
  } finally {
    reader.releaseLock()
  }
  const bytes = new Uint8Array(length)
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.byteLength
  }
  return bytes.buffer
}

async function resolveBytes(
  request: AnalyzeBacktestRequest,
  source: TrustedArtifactSource,
  options: RunAnalysisOptions,
): Promise<{ bytes: ArrayBuffer; relatedBytes?: ArrayBuffer } | AnalysisResponse> {
  if (source.executionId !== request.executionId || source.artifact !== request.artifact) {
    return {
      ...request,
      status: 'unavailable',
      ok: false,
      reason: 'missing_artifact',
      message: 'The resolved artifact does not belong to the requested execution.',
      counts: { scanned: 0, matched: 0, returned: 0 },
      completeness: { complete: false, truncated: false },
    }
  }
  const bytes = bytesFromSource(source)
  let primary: ArrayBuffer
  if (bytes) {
    primary = bytes
    const size = primary.byteLength
    if (size > ANALYSIS_LIMITS.maxInputBytes) {
      return limit(
        request,
        `Artifact exceeds ${ANALYSIS_LIMITS.maxInputBytes} bytes.`,
        'Use a smaller owned artifact.',
      )
    }
  } else {
    if (!source.url) {
      return {
        ...request,
        status: 'unavailable',
        ok: false,
        reason: 'missing_artifact',
        message: `No owned ${request.artifact} artifact was captured for this execution.`,
        counts: { scanned: 0, matched: 0, returned: 0 },
        completeness: { complete: false, truncated: false },
      }
    }
    const response = await (options.fetcher ?? fetch)(source.url, { signal: options.signal })
    const resolved = await readResponseBytes(response, request)
    if (!(resolved instanceof ArrayBuffer)) return resolved
    primary = resolved
  }

  if (request.operation !== 'signal_window' || !source.related) return { bytes: primary }
  const related = source.related
  if (related.executionId !== request.executionId || related.artifact !== 'transactions') {
    return {
      ...request,
      status: 'unavailable',
      ok: false,
      reason: 'missing_artifact',
      message: 'The resolved transactions artifact does not belong to the requested execution.',
      counts: { scanned: 0, matched: 0, returned: 0 },
      completeness: { complete: false, truncated: false },
    }
  }
  const relatedBytes = bytesFromSource(related)
  let resolvedRelated: ArrayBuffer
  if (relatedBytes) {
    resolvedRelated = relatedBytes
    if (resolvedRelated.byteLength > ANALYSIS_LIMITS.maxInputBytes) {
      return limit(
        request,
        `Related artifact exceeds ${ANALYSIS_LIMITS.maxInputBytes} bytes.`,
        'Use a smaller owned artifact.',
      )
    }
  } else {
    if (!related.url) {
      return {
        ...request,
        status: 'unavailable',
        ok: false,
        reason: 'missing_artifact',
        message: 'No owned transactions artifact was captured for signal-to-fill analysis.',
        counts: { scanned: 0, matched: 0, returned: 0 },
        completeness: { complete: false, truncated: false },
      }
    }
    const response = await (options.fetcher ?? fetch)(related.url, { signal: options.signal })
    const remainingBudget = Math.max(0, ANALYSIS_LIMITS.maxInputBytes - primary.byteLength)
    const resolved = await readResponseBytes(
      response,
      request,
      remainingBudget,
      `Combined artifacts exceed ${ANALYSIS_LIMITS.maxInputBytes} bytes.`,
    )
    if (!(resolved instanceof ArrayBuffer)) return resolved
    resolvedRelated = resolved
  }
  if (primary.byteLength + resolvedRelated.byteLength > ANALYSIS_LIMITS.maxInputBytes) {
    return limit(
      request,
      `Combined artifacts exceed ${ANALYSIS_LIMITS.maxInputBytes} bytes.`,
      'Use smaller owned artifacts or a narrower server-side artifact.',
    )
  }
  return { bytes: primary, relatedBytes: resolvedRelated }
}

function defaultWorkerFactory(): AnalysisWorkerLike {
  return new Worker(new URL('./analysis.worker.ts', import.meta.url), { type: 'module' })
}

/** Resolve a trusted app-owned artifact and run one bounded analysis in a Worker. */
export async function runBacktestAnalysis(
  request: AnalyzeBacktestRequest,
  source: TrustedArtifactSource,
  options: RunAnalysisOptions = {},
): Promise<AnalysisResponse> {
  if (options.signal?.aborted) return cancelled(request)
  let resolved: { bytes: ArrayBuffer; relatedBytes?: ArrayBuffer } | AnalysisResponse
  const acquisitionController = new AbortController()
  const abortAcquisition = () => acquisitionController.abort()
  options.signal?.addEventListener('abort', abortAcquisition, { once: true })
  const acquisitionTimeoutMs = Math.min(
    options.acquisitionTimeoutMs ?? ANALYSIS_LIMITS.maxAcquisitionMs,
    ANALYSIS_LIMITS.maxAcquisitionMs,
  )
  let acquisitionTimer: ReturnType<typeof setTimeout> | undefined
  try {
    const acquisition = resolveBytes(request, source, {
      ...options,
      signal: acquisitionController.signal,
    })
    const timeout = new Promise<never>((_, reject) => {
      acquisitionTimer = setTimeout(() => {
        acquisitionController.abort()
        reject(new AnalysisAcquisitionTimeout())
      }, acquisitionTimeoutMs)
    })
    resolved = await Promise.race([acquisition, timeout])
  } catch (error) {
    if (acquisitionTimer) clearTimeout(acquisitionTimer)
    options.signal?.removeEventListener('abort', abortAcquisition)
    if (options.signal?.aborted) return cancelled(request)
    if (error instanceof AnalysisAcquisitionTimeout) {
      return {
        ...request,
        status: 'error',
        ok: false,
        code: 'decode_failed',
        message: 'Artifact acquisition timed out.',
        counts: { scanned: 0, matched: 0, returned: 0 },
        completeness: { complete: false, truncated: false },
      }
    }
    return {
      ...request,
      status: 'error',
      ok: false,
      code: 'decode_failed',
      message: 'Failed to acquire the owned artifact.',
      counts: { scanned: 0, matched: 0, returned: 0 },
      completeness: { complete: false, truncated: false },
    }
  }
  if (acquisitionTimer) clearTimeout(acquisitionTimer)
  options.signal?.removeEventListener('abort', abortAcquisition)
  if ('status' in resolved) return resolved
  if (options.signal?.aborted) return cancelled(request)

  let worker: AnalysisWorkerLike
  try {
    worker = (options.workerFactory ?? defaultWorkerFactory)()
  } catch (error) {
    return {
      ...request,
      status: 'error',
      ok: false,
      code: 'decode_failed',
      message: error instanceof Error ? error.message : 'Analysis Worker could not start.',
      counts: { scanned: 0, matched: 0, returned: 0 },
      completeness: { complete: false, truncated: false },
    }
  }
  const timeoutMs = Math.min(
    options.timeoutMs ?? ANALYSIS_LIMITS.maxWorkerMs,
    ANALYSIS_LIMITS.maxWorkerMs,
  )
  return await new Promise<AnalysisResponse>((resolve) => {
    let settled = false
    const finish = (response: AnalysisResponse) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      options.signal?.removeEventListener('abort', onAbort)
      worker.terminate()
      resolve(response)
    }
    const onAbort = () => finish(cancelled(request))
    const timeout = setTimeout(
      () =>
        finish({
          ...request,
          status: 'limit',
          ok: false,
          limit: 'maxWorkerMs',
          message: `Analysis exceeded ${timeoutMs}ms.`,
          suggestion: 'Narrow the selected range and try again.',
          counts: { scanned: 0, matched: 0, returned: 0 },
          completeness: { complete: false, truncated: true, reason: 'worker_timeout' },
        }),
      timeoutMs,
    )
    worker.onmessage = (event) => {
      if (event.data?.type === 'result') finish(event.data.response as AnalysisResponse)
      else
        finish({
          ...request,
          status: 'error',
          ok: false,
          code: 'decode_failed',
          message: 'Worker returned an invalid result.',
          counts: { scanned: 0, matched: 0, returned: 0 },
          completeness: { complete: false, truncated: false },
        })
    }
    worker.onerror = () =>
      finish({
        ...request,
        status: 'error',
        ok: false,
        code: 'decode_failed',
        message: 'Analysis Worker failed.',
        counts: { scanned: 0, matched: 0, returned: 0 },
        completeness: { complete: false, truncated: false },
      })
    options.signal?.addEventListener('abort', onAbort, { once: true })
    const transfer: Transferable[] = [resolved.bytes]
    if (resolved.relatedBytes) transfer.push(resolved.relatedBytes)
    worker.postMessage(
      { type: 'analyze', request, bytes: resolved.bytes, relatedBytes: resolved.relatedBytes },
      transfer,
    )
  })
}
